import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailVerificationService } from '../../services/emailVerificationService';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CodeInput from './components/CodeInput';
import VerificationStatus from './components/VerificationStatus';

const EmailVerificationPortal = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verificationStatus, setVerificationStatus] = useState('pending');
  const [userEmail, setUserEmail] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(900); // 15 minutes in seconds
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // Load verification status if user is authenticated
    if (user) {
      loadVerificationStatus();
    }
  }, [user]);

  useEffect(() => {
    // Countdown timer for code expiration
    if (timeRemaining > 0 && verificationStatus === 'pending') {
      const timer = setTimeout(() => setTimeRemaining(timeRemaining - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeRemaining, verificationStatus]);

  useEffect(() => {
    // Resend cooldown timer
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const loadVerificationStatus = async () => {
    const { data, error } = await emailVerificationService?.checkVerificationStatus();
    if (!error && data) {
      setUserEmail(data?.email);
      if (data?.emailVerified) {
        setVerificationStatus('verified');
        setTimeout(() => navigate('/user-wallet-dashboard'), 2000);
      }
    }
  };

  const handleCodeChange = (newCode) => {
    setCode(newCode);
    setError('');

    // Auto-submit when all 6 digits are entered
    if (newCode?.every(digit => digit !== '')) {
      handleVerify(newCode?.join(''));
    }
  };

  const handleVerify = async (verificationCode) => {
    const codeString = verificationCode || code?.join('');
    
    if (codeString?.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error } = await emailVerificationService?.verifyEmailToken(codeString);
      
      if (error) {
        setError(error?.message);
        setVerificationStatus('error');
      } else if (data?.verified) {
        setVerificationStatus('verified');
        setTimeout(() => navigate('/user-wallet-dashboard'), 2000);
      }
    } catch (err) {
      setError(err?.message || 'Verification failed');
      setVerificationStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setResendLoading(true);
    setError('');

    try {
      const { error } = await emailVerificationService?.resendVerificationEmail();
      
      if (error) {
        setError(error?.message);
      } else {
        setResendCooldown(60); // 60 second cooldown
        setTimeRemaining(900); // Reset timer
        setCode(['', '', '', '', '', '']);
        setVerificationStatus('pending');
      }
    } catch (err) {
      setError(err?.message || 'Failed to resend code');
    } finally {
      setResendLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const maskEmail = (email) => {
    if (!email) return '';
    const [local, domain] = email?.split('@');
    const maskedLocal = local?.charAt(0) + '***' + local?.charAt(local?.length - 1);
    return `${maskedLocal}@${domain}`;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
          {/* Header */}
          <div className="flex items-center justify-center mb-6">
            <div className="bg-primary rounded-full p-3">
              <img src="/assets/images/22458-1768718790784.png" alt="BOLTPay Logo" className="w-8 h-8 object-contain" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-center text-foreground mb-2">
            Verify Your Email
          </h1>
          <p className="text-center text-muted-foreground mb-6">
            We've sent a 6-digit verification code to
          </p>
          <p className="text-center text-foreground font-semibold mb-6">
            {maskEmail(userEmail)}
          </p>

          {/* Verification Status */}
          <VerificationStatus status={verificationStatus} />

          {/* Code Input */}
          {verificationStatus === 'pending' && (
            <>
              <CodeInput 
                code={code} 
                onChange={handleCodeChange}
                disabled={loading}
              />

              {/* Timer */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <Icon name="Clock" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Code expires in: <span className="font-semibold text-foreground">{formatTime(timeRemaining)}</span>
                </span>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-error/10 border border-error rounded-md p-3 flex items-start gap-2 mb-6">
                  <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              {/* Verify Button */}
              <Button
                type="button"
                fullWidth
                loading={loading}
                onClick={() => handleVerify()}
                disabled={loading || code?.some(digit => digit === '')}
                iconName="CheckCircle"
                className="mb-4"
              >
                {loading ? 'Verifying...' : 'Verify Email'}
              </Button>

              {/* Resend Code */}
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  Didn't receive the code?
                </p>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || resendLoading}
                  className="text-primary hover:underline font-semibold text-sm disabled:text-muted-foreground disabled:no-underline"
                >
                  {resendLoading ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                </button>
              </div>

              {/* Help Section */}
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <div className="flex items-start gap-2">
                  <Icon name="Info" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-semibold text-foreground mb-1">Troubleshooting Tips:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Check your spam or junk folder</li>
                      <li>Make sure the email address is correct</li>
                      <li>Wait a few minutes for the email to arrive</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Back to Sign In */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/signin')}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPortal;